import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, KeyRound, Mail, Calendar, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

export default function MyProfile() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [pwSending, setPwSending] = useState(false);

  useEffect(() => { setName(user?.name || ""); }, [user?.name]);

  const save = async () => {
    if (!name.trim()) return toast.error("Name can't be empty");
    if (name.trim() === user?.name) return;
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/me", { name: name.trim() });
      setUser(data);
      toast.success("Profile updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const sendPasswordReset = async () => {
    if (!user?.email) return;
    setPwSending(true);
    try {
      await api.post("/auth/forgot-password", { email: user.email });
      toast.success("Password reset link sent — check your inbox.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPwSending(false); }
  };

  const role = user?.role === "super_admin" ? "owner" : user?.role;

  return (
    <DashboardLayout role={role}>
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-orange mb-2">Account</div>
        <h1 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight">My profile</h1>
        <p className="text-foreground/60 mt-2 text-sm">Update your personal details and manage your password.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-6 border border-border lg:col-span-2 space-y-5" data-testid="my-profile-card">
          <div>
            <Label htmlFor="me-name">Full name</Label>
            <Input
              id="me-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1.5"
              data-testid="me-name-input"
              maxLength={120}
            />
          </div>
          <div>
            <Label>Email address</Label>
            <Input readOnly value={user?.email || ""} className="mt-1.5 font-mono text-sm bg-muted" data-testid="me-email-readonly" />
            <div className="text-xs text-muted-foreground mt-1.5">
              Email is used for login. To change it, contact support at
              {" "}<a href="mailto:info@menumojo.in" className="brand-orange font-semibold">info@menumojo.in</a>.
            </div>
          </div>
          <div className="flex flex-wrap gap-3 pt-2">
            <Button
              onClick={save}
              disabled={saving || !name.trim() || name.trim() === user?.name}
              className="bg-brand-orange hover:bg-[#E65C00] text-white rounded-full"
              data-testid="me-save-button"
            >
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
            </Button>
            <Button
              variant="outline"
              onClick={sendPasswordReset}
              disabled={pwSending}
              className="rounded-full"
              data-testid="me-change-password-btn"
            >
              <KeyRound className="w-4 h-4" /> {pwSending ? "Sending…" : "Email me a password reset link"}
            </Button>
          </div>
        </Card>

        <Card className="p-6 border border-border h-fit">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-4">At a glance</div>
          <dl className="space-y-4 text-sm">
            <div className="flex items-start gap-3">
              <Mail className="w-4 h-4 mt-0.5 text-foreground/50" />
              <div><dt className="text-xs text-muted-foreground">Email</dt><dd className="font-medium break-all">{user?.email}</dd></div>
            </div>
            <div className="flex items-start gap-3">
              <BadgeCheck className="w-4 h-4 mt-0.5 text-foreground/50" />
              <div><dt className="text-xs text-muted-foreground">Role</dt><dd className="font-medium capitalize">{user?.role?.replace("_", " ")}</dd></div>
            </div>
            {user?.created_at && (
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 mt-0.5 text-foreground/50" />
                <div><dt className="text-xs text-muted-foreground">Member since</dt><dd className="font-medium">{new Date(user.created_at).toLocaleDateString()}</dd></div>
              </div>
            )}
          </dl>
        </Card>
      </div>
    </DashboardLayout>
  );
}
